<template>
  <header
    class="site-header"
  >
    <div
      class="site-header__container"
      :class="{ 'site-header__container--raised': mobileSearchOpened }"
    >
      <!-- Left -->
      <div class="site-header__left">
        <navigation
          :desktop="desktop"
          :parent-settings="settings"
          :blocks="section.blocks"
          @handle-megamenu="handleMegamenu"
        />

        <button
          v-if="mobile.handle"
          class="site-header__menu icon-button"
          type="button"
          @click.prevent="handleMenuToggle"
        >
          <span
            class="visually-hidden"
            v-text="$string('general.site_header.menu')"
          />

          <icon-menu />
        </button>

        <button
          class="site-header__menu icon-button"
          type="button"
          @click="toggleMobileSearchOverlay"
        >
          <span
            class="visually-hidden"
            v-text="$string('general.site_header.search')"
          />

          <icon-search v-if="isSearchOpen" />
          <icon-close v-else />
        </button>
      </div>

      <!-- Logo -->
      <component
        :is="titleElement"
        class="site-header__center"
      >
        <a
          class="site-header__logo"
          :href="$variable('routes.root')"
        >
          <span
            id="site-header-title"
            class="visually-hidden"
            v-text="$variable('store.shopName')"
          />

          <brand-logo />
        </a>
      </component>

      <!-- Right -->
      <div class="site-header__right">
        <form
          class="site-header__search"
          :action="$variable('routes.search')"
          method="GET"
        >
          <icon-search
            v-if="searchStatus === 'ready'"
          />

          <button
            v-if="searchStatus === 'done'"
            type="button"
            class="site-header__search-clear text-body-xs-regular"
            @click="clearSearch"
            v-text="$string('predictiveSearch.clear')"
          />

          <input
            v-model="searchTerm"
            class="site-header__search-input"
            type="search"
            name="q"
            :placeholder="$string('general.site_header.search')"
            @input="toggleSearchOverlay"
          >
        </form>

        <a
          class="site-header__account icon-button"
          :href="$variable('routes.account.url')"
        >
          <span
            class="visually-hidden"
            v-text="$string('general.site_header.account')"
          />

          <icon-account />
        </a>

        <a
          class="site-header__cart icon-button"
          :href="$variable('routes.cart.url')"
          @click="handleCartToggle"
        >
          <span
            class="visually-hidden"
            v-text="$string('general.site_header.cart')"
          />

          <icon-cart />

          <span
            v-if="cart.item_count > 0"
            class="site-header__cart-badge critical-clear text-body-2xs-regular"
            aria-hidden="true"
            v-text="cart.item_count"
          />
        </a>
      </div>
    </div>

    <!-- Search -->
    <overlay
      key="searchShelf"
      block-class="search-shelf"
      :direction="isMobile ? 'right' : 'down'"
      namespace="searchShelf"
      :template="false"
      type="shelf"
    >
      <template #body="overlay">
        <predictive-search
          :search-term="searchTerm"
          :overlay="overlay"
          @close-search-modal="clearSearch"
          @clear-mobile-search="clearMobileSearch"
          @update-search-value="updateSearchValue"
        />
      </template>
    </overlay>

    <!-- Megamenu -->
    <overlay
      key="megamenuShelf"
      block-class="megamenu-shelf"
      direction="down"
      namespace="megamenuShelf"
      :template="false"
      type="shelf"
      @before-close="handleMegamenuClose"
    >
      <template #body="overlay">
        <div class="megamenu">
          <template v-if="activeMegamenu">
            <nav class="megamenu__list">
              <div
                v-if="activeMegamenu.topBarItems.length > 1"
                class="megamenu__top-bar"
              >
                <template v-for="(item, index) of activeMegamenu.topBarItems" :key="index">
                  <div class="megamenu__top-bar-item">
                    <button
                      class="text-body-m-bold"
                      :class="{ [$class('active')]: activeTopBarItem === item }"
                      type="button"
                      @click="handleTopBarItemClick(index)"
                      v-text="item.title"
                    />
                  </div>

                  <span
                    v-if="index < activeMegamenu.topBarItems.length - 1"
                    class="megamenu__top-bar-divider"
                    aria-hidden="true"
                  />
                </template>
              </div>

              <div class="megamenu__content">
                <template v-if="activeTopBarItem">
                  <ul class="megamenu__link-wrapper">
                    <li
                      v-for="link of activeTopBarItem.content"
                      :key="link.url"
                      class="megamenu__item"
                    >
                      <a
                        class="megamenu__link text-heading-xl"
                        :href="link.url"
                      >
                        <div
                          v-if="link.image.src"
                          class="megamenu__link-image"
                        >
                          <responsive-image
                            :image="link.image"
                            ratio="70:85"
                          />
                        </div>

                        <div class="megamenu__link-content">
                          <div class="megamenu__link-title--badge">
                            <div
                              class="megamenu__link-title text-body-s-bold"
                              v-text="link.title.split(' | ')[0]"
                            />

                            <div
                              v-if="link.badge"
                              class="megamenu__link-badge text-other-button-s"
                              v-text="link.badge"
                            />
                          </div>

                          <div
                            v-if="link.description"
                            class="megamenu__link-description text-body-xs-regular"
                            v-text="link.description"
                          />
                        </div>
                      </a>
                    </li>
                  </ul>

                  <div class="megamenu__media-wrapper">
                    <media-block
                      v-for="(media, index) of activeTopBarItem.media"
                      :key="index"
                      :media="media"
                      :ratio="0.82"
                      block-class="megamenu"
                    />
                  </div>
                </template>
              </div>
            </nav>
          </template>
        </div>
      </template>
    </overlay>

    <!-- Menu drawer -->
    <teleport
      v-if="mobile.handle"
      to="body"
    >
      <overlay
        key="menuDrawer"
        block-class="menu-drawer-overlay"
        direction="right"
        namespace="menuDrawer"
        type="drawer"
        :show-close="false"
      >
        <template #body="overlay">
          <menu-drawer
            v-if="getComponentVisible('menu-drawer')"
            v-cloak
            :handle="mobile.handle"
            :item-count="mobile.itemCount"
            :overlay="overlay"
            :secondary="mobile.secondary"
            :mobile="{
              handle: mobile.handle,
              itemCount: mobile.itemCount,
              secondary: mobile.secondary,
              blocks: mobile.blocks
            }"
            :current-country="currentCountry"
            :current-language="currentLanguage"
            :localization-countries="localizationCountries"
            :flag-icons="flagIcons"
            :custom-us-country-codes="customUsCountryCodes"
            :theme-custom-country-codes="themeCustomCountryCodes"
          />

          <div
            v-if="!getComponentActive('menu-drawer')"
            class="menu-drawer navigation__menu-drawer"
          >
            <div class="menu-drawer__header">
              <a
                :href="$variable('routes.root')"
                class="menu-drawer__left"
              >
                <span
                  class="visually-hidden"
                  v-text="$variable('store.shopName')"
                />

                <brand-logo />
              </a>

              <div class="menu-drawer__center"></div>

              <div class="menu-drawer__right">
                <button
                  class="menu-drawer__close icon-button"
                  type="button"
                >
                  <icon-close />
                </button>
              </div>
            </div>

            <div
              v-for="(_, index) of [...Array(mobile.itemCount)]"
              :key="`menu-drawer-loading-${index}`"
              class="navigation__menu-drawer-link"
            >
              <span class="navigation__menu-drawer-loading" />
            </div>
          </div>
        </template>
      </overlay>
    </teleport>
  </header>
</template>

<script>

/**
 * Vue: Site header (site-header)
 * -----------------------------------------------------------------------------
 * Site header central banner containing logo, icon buttons, and menu drawer.
 * - No style import as it's included in critical stylesheet.
 *
 * @param {Object} liquid - Liquid section ID, used for theme editor events.
 * @param {Object} desktop - Desktop linklist handle and items.
 * @param {Object} mobile - Mobile linklist handle, subdrawer settings and item count.
 * @param {String} titleElement - HTML element of site logo based on page.
 * @param {String} currentCountry - Localization country ISO code.
 * @param {Array} localizationCountries - Array of localization countries.
 * @param {Object} flagIcons - Object of svg country flags.
 *
 */
import { defineAsyncComponent } from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'

import Overlay from '~global/overlay/overlay'
import Navigation from '~global/site-header-group/navigation/navigation'

import PredictiveSearch from '~async/predictive-search/predictive-search'
import BrandLogo from '~icons/general/brand-logo.svg'
import IconAccount from '~icons/general/account.svg'
import IconCart from '~icons/general/cart.svg'
import IconMenu from '~icons/general/menu.svg'
import IconClose from '~icons/directional-navigation/close.svg'
import IconSearch from '~icons/general/search.svg'
import MediaBlock from '~async/menu-drawer/media-block'
import ResponsiveImage from '~global/images/responsive-image'

export default {
  name: 'SiteHeader',

  components: {
    BrandLogo,
    IconAccount,
    IconCart,
    IconMenu,
    IconClose,
    IconSearch,
    Navigation,
    MenuDrawer: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.menu-drawer' */
        '~async/menu-drawer/menu-drawer'
      ),
    }),
    Overlay,
    PredictiveSearch,
    MediaBlock,
    ResponsiveImage,
  },

  props: {
    // Needed for theme editor events
    // eslint-disable-next-line vue/no-unused-properties
    liquid: {
      type: Object,
      default: () => ({}),
    },
    desktop: {
      type: String,
      default: '',
    },
    mobile: {
      type: Object,
      default: () => ({}),
    },
    titleElement: {
      type: String,
      default: 'span',
    },
    currentCountry: {
      type: String,
      default: '',
    },
    currentLanguage: {
      type: String,
      default: '',
    },
    localizationCountries: {
      type: Array,
      default: () => [],
    },
    flagIcons: {
      type: Object,
      default: () => ({}),
    },
    customUsCountryCodes: {
      type: Array,
      default: () => [],
    },
    themeCustomCountryCodes: {
      type: Array,
      default: () => [],
    },
    settings: {
      type: Object,
      default: () => ({
        enableMegamenu: true,
        megamenuStyle: 'shelf',
        megamenuColumns: 4
      }),
    },
    blocks: {
      type: Array,
      default: () => [],
    },
    section: {
      type: Object,
      default: () => ({}),
    }
  },

  data() {
    return {
      searchTerm: '',
      searchStatus: 'ready',
      mobileSearchOpened: false,
      activeMegamenu: null,
      activeTopBarItem: null,
      activeTopBarItemIndex: null,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
      getComponentExists: 'components/getExists',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
      isMobile: (state) => state.index.screen.mobile,
    }),

    isSearchOpen() {
      if (this.getActiveOverlay === 'searchShelf') {
        return false
      }

      return true
    },

    /**
     * Get current section data.
     * @returns {Object} Section data
     */
    currentSection() {
      return this.section || {}
    },

    /**
     * Get mega menu block settings.
     * @returns {Object} Mega menu block settings
     */
    megaMenuBlock() {
      if (!this.activeMegamenu) return null
      return this.currentSection.blocks?.find(block =>
        block.type === 'navigation' &&
        block.settings.handle === this.activeMegamenu.title
      )
    },
  },

  mounted() {
    this.setMegamenuTopPosition()
    window.addEventListener('resize', this.setMegamenuTopPosition)
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.setMegamenuTopPosition)
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
      toggleOverlay: 'overlays/toggle',
      setSearchStatus: 'search/setSearchStatus',
    }),

    /**
     * Localize a URL to include the proper localization prefix.
     * @param {String} url - The URL to localize
     * @returns {String} The localized URL
     */
    localizeUrl(url) {
      if (!url) return url

      if (url.startsWith('http')) {
        return url
      }

      // If it's already localized (starts with /de-de, /fr-fr, etc.), return as is
      if (url.match(/^\/[a-z]{2}-[a-z]{2}/)) {
        return url
      }

      const normalizedRoot = cnvs.routes.normalisedRoot
      const cleanUrl = url.startsWith('/') ? url.slice(1) : url
      return `${normalizedRoot}${cleanUrl}`
    },

    /**
     * Handle menu toggle.
     */
    handleMenuToggle() {
      this.toggleOverlay({
        component: 'menu-drawer',
        ignoreDismissed: true,
        namespace: 'menuDrawer',
      })

      this.resetSearchOverlay()
    },

    /**
     * Handle cart toggle.
     * - Prevents opening cart drawer on cart page.
     * @param {Object} event - Click event.
     */
    handleCartToggle(event) {
      if (
        this.$variable('page.type') === 'cart' ||
        !this.getComponentExists('cart-drawer')
      ) {
        return
      }

      event.preventDefault()

      this.toggleOverlay({
        component: 'cart-drawer',
        ignoreDismissed: true,
        namespace: 'cartDrawer',
      })

      this.resetSearchOverlay()
    },

    /**
     * Shopify section editor select event.
     */
    shopifySectionSelect() {
      if (
        !this.isMobile ||
        !this.getComponentExists('menu-drawer')
      ) {
        return
      }

      this.openOverlay({
        component: 'menu-drawer',
        ignoreDismissed: true,
        namespace: 'menuDrawer',
      })
    },

    /**
     * Shopify section editor deselect event.
     */
    shopifySectionDeselect() {
      this.closeOverlay('menuDrawer')
    },

    /**
     * Toggle predictive search overlay.
     */
    toggleSearchOverlay() {
      if (this.searchTerm) {
        this.openOverlay({
          ignoreDismissed: true,
          namespace: 'searchShelf',
        })

      } else {
        this.closeOverlay('searchShelf')
      }
    },

    /**
     * Toggle mobile search overlay.
     */
    toggleMobileSearchOverlay() {
      this.mobileSearchOpened = !this.mobileSearchOpened
      this.searchTerm = ''
      this.setSearchStatus('ready')

      this.toggleOverlay({
        ignoreDismissed: true,
        namespace: 'searchShelf',
      })
    },

    /**
     * Clear search input, change search status and close overlay.
     */
    clearSearch() {
      this.searchTerm = ''
      this.closeOverlay('searchShelf')
      this.setSearchStatus('ready')
    },

    /**
     * Clear search term on the mobile search overlay.
     */
    clearMobileSearch() {
      this.searchTerm = ''
    },

    /**
     * Update search value.
     * @param {String} value - search value.
     */
    updateSearchValue(value) {
      this.searchTerm = value
    },

    /**
     * Resets search overlay to ready state.
     */
    resetSearchOverlay() {
      this.mobileSearchOpened = false
      this.setSearchStatus('ready')
    },

    /**
     * Handle megamenu data from navigation component.
     * @param {Object} data - Megamenu data
     */
    handleMegamenu(data) {
      if (!data || !data.children) return

      this.setMegamenuTopPosition()

      // Find the corresponding mega menu block
      const megaMenuBlock = this.section?.blocks?.find(block => {
        return block.type === 'navigation' && block.settings?.handle === data.title
      })

      if (megaMenuBlock && megaMenuBlock.settings.subSettings.length > 0) {
        // Create top bar items from subSettings
        const topBarItems = megaMenuBlock.settings.subSettings.map(subSetting => {
          // Transform mediaBlocks to media format (desktop)
          const media = subSetting.mediaBlocks?.map(mediaBlock => ({
            image: mediaBlock.image,
            logo: mediaBlock.logo,
            title: mediaBlock.title,
            subtitle: mediaBlock.subtitle,
            ctaTitle: mediaBlock.btnLabel,
            ctaLink: this.localizeUrl(mediaBlock.btnUrl)
          })).filter(media => media.image) || []

          // Transform linkBlocks to content format (shared)
          const content = subSetting.linkBlocks?.map(linkBlock => ({
            title: linkBlock.title,
            description: linkBlock.subtitle,
            badge: linkBlock.badge,
            url: this.localizeUrl(linkBlock.linkUrl),
            image: linkBlock.image
          })) || []

          return {
            title: subSetting.title,
            content: content,
            media: media
          }
        }).filter(item => item.title && item.title.trim() !== '')

        // Set the active mega menu
        this.activeMegamenu = {
          title: data.title,
          style: data.style,
          columns: data.columns,
          topBarItems: topBarItems
        }

        // Set the first top bar item as active
        this.activeTopBarItem = topBarItems[0]
        this.activeTopBarItemIndex = 0
      } else {
        window.location.href = data.url
      }
    },

    /**
     * Handle top bar item click
     * @param {number} index - Index of the clicked item
     */
    handleTopBarItemClick(index) {
      this.activeTopBarItem = this.activeMegamenu.topBarItems[index]
    },

    /**
     * Handle megamenu close.
     */
    handleMegamenuClose() {
      this.activeMegamenu = null
      this.activeTopBarItem = null
      this.activeTopBarItemIndex = null
    },

    /**
     * Calculate and set the megamenu top position dynamically.
     * This prevents overlap when navigation text wraps to multiple lines.
     */
    setMegamenuTopPosition() {
      const siteHeaderElement = document.querySelector('.site-header')
      if (siteHeaderElement) {
        const headerHeight = siteHeaderElement.offsetHeight
        document.documentElement.style.setProperty('--megamenu-top-offset', `${headerHeight - 1}px`)
      }
    },
  },
}
</script>
